<template>
  <div class="create-edit-infoitem">
    <v-card rounded="xl" elevation="4" class="mx-4 mt-4 mb-4 px-4 py-3"
      style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);">
      <div class="d-flex align-center">
        <v-btn icon variant="text" color="white" size="small" @click="goBack">
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="ml-3 text-h6 font-weight-semibold" style="color: #ffffff;">
          {{ $t('news.new-post') }}
        </div>
        <v-spacer />
        <img :src="logo" class="toolbar-icon-white" />
      </div>
    </v-card>
    <Loader :loading="showLoader" />
    <div class="create-edit-info-item">
      <v-container fluid>
        <v-form ref="form" lazy-validation>
          <!-- Title -->
          <v-text-field v-model="title" :rules="[rules.required, rules.titleLimit]" :label="$t('news.title')"
            prepend-inner-icon="mdi-format-title" variant="underlined" color="primary" counter="100"
            @change="isTitleChanged = true" required></v-text-field>

          <!-- Category Dropdown -->
          <v-select v-model="category" :items="categories" :disabled="isEdit" :label="$t('news.category')"
            prepend-inner-icon="mdi-tag" variant="underlined" color="primary" @change="updateCategory"
            required></v-select>

          <!-- Event Fields -->
          <template v-if="category === 'Event'">
            <!-- Event Date Picker -->
            <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="290px">
              <template v-slot:activator="{ props }">
                <v-text-field v-bind="props" v-model="eventDateText" :label="$t('news.event-date')"
                  prepend-inner-icon="mdi-calendar" variant="underlined" color="primary" readonly required
                  @change="isEventDateChanged = true"></v-text-field>
              </template>
              <v-date-picker color="primary" max-width="250px" max-height="350px" min-width="250px" elevation="3"
                rounded="lg" hide-header show-adjacent-months v-model="eventDatePickerModel"
                @update:modelValue="updateEventDate"></v-date-picker>
            </v-menu>

            <!-- Short Text Content for Event -->
            <v-textarea v-model="shortTextContent" :rules="[rules.required, rules.lineLimit]"
              :label="$t('news.short-text')" prepend-inner-icon="mdi-text" variant="underlined" color="primary" rows="1"
              @change="isShortTextContentChanged = true" counter="250" required></v-textarea>
          </template>

          <!-- News and Announcement Fields -->
          <template v-if="category === 'News' || category === 'Announcement'">
            <!-- Short Description (v-text-field) -->
            <v-text-field v-model="shortDescription" :rules="[rules.required, rules.shortTextLimit]"
              :label="$t('news.short-description')" prepend-inner-icon="mdi-text" variant="underlined" color="primary"
              counter="150" @change="isShortDescriptionChanged = true" required></v-text-field>

            <!-- Full Description (v-textarea) -->
            <v-textarea v-model="textContent" :label="$t('news.description')" prepend-inner-icon="mdi-format-align-left"
              variant="underlined" color="primary" @change="isTextContentChange = true" rows="5" required></v-textarea>
          </template>

          <!-- Upload Section (Hidden for Event Category) -->
          <div v-if="category !== 'Event'" class="upload-section">
            <h4>{{ $t('news.images-files') }}</h4>

            <!-- Title Image -->
            <v-file-input v-model="titleImage" :label="$t('news.title-image')" accept="image/png, image/jpeg"
              prepend-icon="mdi-image" variant="underlined" @update:modelValue="onTitleImageChange" color="primary">
              <template v-slot:selection="{ fileNames }">
                <template v-for="fileName in fileNames" :key="fileName">
                  <v-chip class="me-2" color="primary" size="small" label>
                    {{ fileName?.substring(0, 30) }}
                  </v-chip>
                </template>
              </template>
            </v-file-input>
            <p class="file-info-text">{{ $t('news.file-format') }}
            </p>

            <!-- Additional Images -->
            <v-file-input v-model="additionalImages" :label="$t('news.additional-images')"
              accept="image/png, image/jpeg" prepend-icon="mdi-folder-multiple-image" variant="underlined"
              color="primary" @update:modelValue="onAdditionalImagesChange" multiple counter show-size>
              <template v-slot:selection="{ fileNames }">
                <template v-for="fileName in fileNames" :key="fileName">
                  <v-chip class="me-2" color="primary" size="small" label>
                    {{ fileName?.substring(0, 30) }}
                  </v-chip>
                </template>
              </template>
            </v-file-input>

            <!-- Files (PDF only) -->
            <v-file-input v-model="files" :label="$t('news.pdf-only')" accept="application/pdf"
              prepend-icon="mdi-file-pdf-box" variant="underlined" color="primary" multiple counter
              @update:modelValue="onFilesChange" show-size>
              <template v-slot:selection="{ fileNames }">
                <template v-for="fileName in fileNames" :key="fileName">
                  <v-chip class="me-2" color="primary" size="small" label>
                    {{ fileName?.substring(0, 30) }}
                  </v-chip>
                </template>
              </template>
            </v-file-input>
            <p class="file-info-text">{{ $t('news.pdf-max') }}</p>
          </div>

          <!-- Submit Button -->
          <v-btn v-if="!isEdit" @click="submitFirestore" color="primary" :disabled="!formValid" class="mt-4">{{
            $t('news.submit')
            }}</v-btn>
          <v-btn v-else @click="editForm" color="primary" :disabled="!formValid || !isAnythingChanged" class="mt-4">{{
            $t('news.update') }}</v-btn>
        </v-form>
      </v-container>
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch, computed } from 'vue'
import Swal from '@/services/ui/dialog'
import { getToolbarLogoImage } from '@/assets/Branding/branding'
import moment from 'moment'
import { showToast } from '@/services/utils'
import { htmlToText } from 'html-to-text'
import Loader from '@/components/general/Loader.vue'
import { loadConfig } from '@/configLoader'
import { getApps, initializeApp, getApp } from 'firebase/app'
import { getFirestore, doc, setDoc, serverTimestamp, collection } from 'firebase/firestore'
import { getFirebaseConfig } from '@/controllers/BaseController'

import { Capacitor } from '@capacitor/core'

import lodash, { remove } from 'lodash'
import { useRoute, useRouter } from 'vue-router'
import {
  createInfoItem,
  linkInfoItemToCategory,
  postFile,
  linkInfoItemTitleImage,
  postMultipleFiles,
  getFile,
  getInfoCategories,
  updateInfoItem,
  linkInfoTitleExtraImages,
  linkInfoItemAttachments,
  deleteInfoItemFiles,
  getFileInfo,
  deleteInfoItem,
  getInfoItemById,
  getInfoCategoryById,
  getInfoItems,
} from '@/controllers/BaseController'
import { removeStoredItem } from '@/services/utils'
import { load } from 'webfontloader'


export default defineComponent({
  name: 'CreateEditInfoItem',
  components: {
    Loader,
  },
  setup() {
    const logo = ref('')
    const title = ref('')
    const route = useRoute()
    const router = useRouter()
    const category = ref('')
    const apiInfoCategories = ref([])
    const eventDateText = ref('')
    const eventDatePickerModel = ref(new Date())
    const shortDescription = ref('')
    const shortTextContent = ref('')
    const textContent = ref('')
    const titleImage = ref(null)
    const additionalImages = ref([])
    const files = ref([])
    const isTitleChanged = ref(false)
    const isEventDateChanged = ref(false)
    const isShortDescriptionChanged = ref(false)
    const isShortTextContentChanged = ref(false)
    const isTextContentChange = ref(false)
    const isTitleImageChanged = ref(false)
    const isAdditionalImagesChanged = ref(false)
    const isFilesChanged = ref(false)
    const firebaseConfig = ref(null)

    const categories = ref(['Event', 'News', 'Announcement'])
    const menu = ref(false)
    const showLoader = ref(false)
    const form = ref(null)
    const originalInfoItem = ref(null)

    const rules = {
      required: value => !!value || 'Required.',
      lineLimit: value => value.split('\n').length <= 1 || 'Only 1 line allowed.',
      titleLimit: value => value.length <= 100 || 'Maximum 100 characters allowed.',
      shortTextLimit: value => value.length <= 150 || 'Maximum 150 characters allowed.',
    }
    const onTitleImageChange = newValue => {
      isTitleImageChanged.value = true
      console.log('Title Image Changed:', newValue)
    }

    const onAdditionalImagesChange = newValue => {
      isAdditionalImagesChanged.value = true
      console.log('Additional Images Changed:', newValue)
    }

    const onFilesChange = newValue => {
      isFilesChanged.value = true
      console.log('Files Changed:', newValue)
    }

    const goBack = () => {
      window.history.back();
    };

    const updateCategory = newValue => {
      category.value = newValue
    }

    const isAnythingChanged = computed(() => {
      return (
        isTitleChanged.value ||
        isEventDateChanged.value ||
        isShortDescriptionChanged.value ||
        isShortTextContentChanged.value ||
        isTextContentChange.value ||
        isTitleImageChanged.value ||
        isAdditionalImagesChanged.value ||
        isFilesChanged.value
      )
    })

    watch(category, newValue => {
      if (newValue && !isEdit.value) {
        title.value = ''
        eventDateText.value = ''
        eventDatePickerModel.value = new Date()
        shortTextContent.value = ''
        shortDescription.value = ''
        textContent.value = ''
        titleImage.value = null
        additionalImages.value = []
        files.value = []
      }
    })

    const formValid = computed(() => {
      return (
        title.value &&
        category.value &&
        (category.value !== 'Event' ? shortDescription.value : true) &&
        (category.value !== 'Event' ? textContent.value : shortTextContent.value)
      )
    })


    const isEdit = computed(() => {
      let params = JSON.parse(JSON.stringify(route.params))
      return params?.id
    })
    const isNew = computed(() => {
      let params = JSON.parse(JSON.stringify(route.params))
      return !params?.id
    })

    const updateEventDate = newDate => {
      eventDatePickerModel.value = newDate
      eventDateText.value = moment(newDate).format('DD-MM-YYYY')
      menu.value = false
    }

    const resizeImage = (file, maxWidth = 750, maxHeight = 498) => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.src = URL.createObjectURL(file)
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height

          if (width > maxWidth) {
            height *= maxWidth / width
            width = maxWidth
          }
          if (height > maxHeight) {
            width *= maxHeight / height
            height = maxHeight
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob(blob => {
            if (blob) {
              // Pass the original file name as well
              resolve(new File([blob], file.name, { type: file.type }))
            } else {
              reject(new Error('Image resize failed.'))
            }
          }, 'image/png')
        }
        img.onerror = () => reject(new Error('Image load failed.'))
      })
    }

    const editForm = async () => {
      if (!form.value || !(await form.value.validate())) return

      Swal.fire({
        title: 'Updating Info Item...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })
      await removeStoredItem('infoItem_' + route.params.id)
      let apiInfoItem = await getInfoItemById(route.params.id)

      try {
        const selectedInfoCategory = apiInfoCategories.value.find(item => item.name.toLowerCase().includes(category.value.toLowerCase()))

        let infoItemPayload = {
          ...apiInfoItem,
          title: title.value,
          active: true,
          version: apiInfoItem.version,
          shortText: category.value !== 'Event' ? shortDescription.value : eventDateText.value,
          text: category.value !== 'Event' ? textContent.value : shortTextContent.value,
          isNews: category.value === 'News',
          infoCategoryId: selectedInfoCategory?.id,
        }

        let { getFormattedHtmlFromText } = await import('@/services/HBC/GeminiHelper.js')
        if (category.value !== 'Event') {
          infoItemPayload.text = await getFormattedHtmlFromText(textContent.value)
        }


        const infoItemId = route.params.id
        const updatedInfoItems = await getInfoItems()
        const updatedInfoItem = updatedInfoItems.find(item => item.id === infoItemId)
        const version = updatedInfoItem.version
        infoItemPayload["version"] = version
        const infoItem = await updateInfoItem(infoItemId, infoItemPayload)

        showToast({ text: 'Info Item updated!', type: 'success' })

        // Handle Title Image
        if (isTitleImageChanged.value) {
          showToast({ text: 'Updating title image...', type: 'default' })
          const titleImageId = infoItem?.files?.entities.find(file => file.type === 'titleimage')?.id
          await deleteInfoItemFiles(infoItemId, [titleImageId])

          if (titleImage.value) {
            const titleImageBlob = await resizeImage(titleImage.value)
            const titleImageResponse = await postFile(titleImageBlob, 'image/png', titleImage.value.name) // Using original file name
            await linkInfoItemTitleImage(infoItemId, titleImageResponse.id)
            showToast({ text: 'Title image updated!', type: 'success' })
          }
        }

        // Handle Additional Images
        if (isAdditionalImagesChanged.value) {
          showToast({ text: 'Updating additional images...', type: 'default' })
          const additionalImagesFiles = infoItem?.files?.entities.filter(file => file.type === 'image')
          const additionalImagesFileIds = additionalImagesFiles.map(file => file.id)
          await deleteInfoItemFiles(infoItemId, additionalImagesFileIds)

          if (additionalImages.value.length > 0) {
            const additionalBlobs = await Promise.all(additionalImages.value.map(file => resizeImage(file)))
            const additionalImagesResponse = await postMultipleFiles(
              additionalBlobs,
              additionalImages.value.map(file => file.name)
            ) // Using original file names
            await linkInfoTitleExtraImages(
              infoItemId,
              additionalImagesResponse.map(file => file.id)
            )
            showToast({ text: 'Additional images updated!', type: 'success' })
          }
        }

        // Handle PDF Files
        if (isFilesChanged.value) {
          showToast({ text: 'Updating PDF files...', type: 'default' })
          const attachmentsFiles = infoItem?.files?.entities.filter(file => file.type === 'attachment')
          const attachmentsFileIds = attachmentsFiles.map(file => file.id)
          await deleteInfoItemFiles(infoItemId, attachmentsFileIds)

          if (files.value.length > 0) {
            const pdfResponse = await postMultipleFiles(files.value)
            await linkInfoItemAttachments(
              infoItemId,
              pdfResponse.map(file => file.id)
            )
            showToast({ text: 'PDF files updated!', type: 'success' })
          }
        }

        Swal.fire({
          title: 'Success!',
          text: 'Info Item updated successfully.',
          icon: 'success',
          confirmButtonColor: '#4caf50', // Light green
          confirmButtonText: 'OK',
        }).then(async () => {
          await removeStoredItem('infoItems')
          await removeStoredItem('infoCategories')
          router.push({ name: 'InfoCenterView' })
        })
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update Info Item.',
          icon: 'error',
        })
      } finally {
        showLoader.value = false
      }
    }


    const submitFirestore = async () => {
      try {
        const db = getFirestore()

        const docRef = doc(collection(db, 'infoItems'))
        const infoItemId = docRef.id

        const isDevEnv = () => {
  const platform = Capacitor.getPlatform()
  if (platform === 'android' || platform === 'ios') return !!Capacitor.DEBUG
  const hostname = window.location.hostname
  return hostname.includes('localhost') || hostname.startsWith('192.168.')
}

        const dummyData = {
          id: infoItemId,
          title: '🔥 Test Broadcast News',
          shortText: 'This is a dummy info item to test FCM topic trigger.',
          category: 'News',
          createdAt: new Date(),
          environment: isDevEnv() ? 'dev' : 'prod',
        }

        await setDoc(docRef, dummyData)

        console.log('✅ Dummy infoItem written with ID:', infoItemId)
      } catch (error) {
        console.log('❌ Error writing dummy infoItem:', error)
      }
    }

    const submitForm = async () => {
      if (!form.value || !(await form.value.validate())) return

      Swal.fire({
        title: 'Creating Info Item...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })

      try {
        const config = await getFirebaseConfig()
        if (!getApps().length) {
          initializeApp(config)
        }

        const selectedInfoCategory = apiInfoCategories.value.find(item =>
          item.name.toLowerCase().includes(category.value.toLowerCase())
        )

        const infoItemPayload = {
          title: title.value,
          active: true,
          shortText: category.value !== 'Event' ? shortDescription.value : eventDateText.value,
          text: category.value !== 'Event' ? textContent.value : shortTextContent.value,
          isNews: category.value === 'News',
          infoCategoryId: selectedInfoCategory?.id,
        }

        const infoItem = await createInfoItem(infoItemPayload)
        const infoItemId = infoItem.id

        showToast({ text: 'Info Item created!', type: 'success' })
        await linkInfoItemToCategory(infoItemId, infoItemPayload.infoCategoryId)

        if (titleImage.value) {
          showToast({ text: 'Uploading title image...', type: 'default' })
          const titleImageBlob = await resizeImage(titleImage.value)
          const titleImageResponse = await postFile(titleImageBlob, 'image/png', titleImage.value.name)
          await linkInfoItemTitleImage(infoItemId, titleImageResponse.id)
          showToast({ text: 'Title image uploaded!', type: 'success' })
        }

        if (additionalImages.value.length) {
          showToast({ text: 'Uploading additional images...', type: 'default' })
          const additionalBlobs = await Promise.all(additionalImages.value.map(file => resizeImage(file)))
          const additionalImagesResponse = await postMultipleFiles(
            additionalBlobs,
            additionalImages.value.map(file => file.name)
          )
          await linkInfoTitleExtraImages(infoItemId, additionalImagesResponse.map(file => file.id))
          showToast({ text: 'Additional images uploaded!', type: 'success' })
        }

        if (files.value.length) {
          showToast({ text: 'Uploading PDF files...', type: 'default' })
          const pdfResponse = await postMultipleFiles(files.value)
          await linkInfoItemAttachments(infoItemId, pdfResponse.map(file => file.id))
          showToast({ text: 'PDF files uploaded!', type: 'success' })
        }

        // ✅ Write to Firestore with correct backend ID
        try {
          const db = getFirestore()
          const docRef = doc(db, 'infoItems', infoItemId)
          const isDevEnv = () =>
            window.location.hostname.includes('localhost') ||
            window.location.hostname.startsWith('192.168.') ||
            import.meta.env.MODE === 'development'

          await setDoc(docRef, {
            id: infoItemId,
            title: infoItemPayload.title,
            shortText: infoItemPayload.shortText,
            category: category.value,
            createdAt: new Date(),
            environment: isDevEnv() ? 'dev' : 'prod',
          })
          console.log('Firestore broadcast document written.')
        } catch (error) {
          console.log('Error writing Firestore infoItem:', error)
        }

        Swal.fire({
          title: 'Success!',
          text: 'Info Item created successfully.',
          icon: 'success',
          confirmButtonColor: '#4caf50',
          confirmButtonText: 'OK',
        }).then(async () => {
          await removeStoredItem('infoItems')
          await removeStoredItem('infoCategories')
          router.push({ name: 'InfoCenterView' })
        })

      } catch (error) {
        console.error('Submit error:', error)
        Swal.fire({
          title: 'Error!',
          text: 'Failed to create Info Item.',
          icon: 'error',
        })
      } finally {
        showLoader.value = false
      }
    }

    onMounted(async () => {
      try {
        logo.value = await getToolbarLogoImage()
        let configs = await loadConfig()
        firebaseConfig.value = configs.firebaseConfig
        console.log(JSON.parse(JSON.stringify(firebaseConfig.value)))
        category.value = 'Event'
        let API_CATEGORIES = await getInfoCategories()
        API_CATEGORIES = API_CATEGORIES.filter(
          category => category.name === 'Events' || category.name === 'News' || category.name === 'Announcement'
        )
        apiInfoCategories.value = API_CATEGORIES
        if (isEdit.value) {
          let { id } = route.params
          let allInfoItems = await getInfoItems()
          let cacheInfoItem = allInfoItems.find(item => item.id === id)
          await removeStoredItem('infoItem_' + cacheInfoItem?.id)
          let infoItem = await getInfoItemById(id)
          let infoCategory = await getInfoCategoryById(infoItem.infoCategory?.id)

          if (infoCategory?.name?.toLowerCase() === 'events') {
            category.value = 'Event'
            title.value = infoItem.title
            eventDateText.value = moment(infoItem.shortText, 'DD-MM-YYYY').format('DD-MM-YYYY')
            eventDatePickerModel.value = moment(infoItem.shortText, 'DD-MM-YYYY').toDate()
            shortTextContent.value = infoItem.text
          } else {
            category.value = infoCategory?.name
            title.value = infoItem.title
            shortDescription.value = infoItem.shortText
            // textContent.value = infoItem.text.replace(/<p>/g, '').replace(/<\/p>/g, '')
            textContent.value = htmlToText(infoItem.text || '', {
              wordwrap: false,
              selectors: [
                { selector: 'a', format: 'inline' },
                { selector: 'img', format: 'skip' }
              ],
            })

            // Fetch and set the title image
            let titleImageFile = infoItem?.files?.entities.find(file => file.type === 'titleimage')
            if (titleImageFile) {
              const fileBlob = await getFile(titleImageFile.id) // Get the actual file (blob)
              const fileInfo = await getFileInfo(titleImageFile.id) // Get the file info (fileName, mimeType, url)
              console.log(fileInfo)
              titleImage.value = new File([fileBlob], fileInfo.fileName, { type: fileInfo.mimeType })
            }

            // Fetch and set additional images
            let additionalImagesFiles = infoItem?.files?.entities.filter(file => file.type === 'image')
            if (additionalImagesFiles.length > 0) {
              additionalImages.value = await Promise.all(
                additionalImagesFiles.map(async file => {
                  const fileBlob = await getFile(file.id) // Get the actual file (blob)
                  const fileInfo = await getFileInfo(file.id) // Get the file info (fileName, mimeType, url)
                  return new File([fileBlob], fileInfo.fileName, { type: fileInfo.mimeType })
                })
              )
            }

            // Fetch and set attachments (PDFs)
            let attachmentsFiles = infoItem?.files?.entities.filter(file => file.type === 'attachment')
            if (attachmentsFiles.length > 0) {
              files.value = await Promise.all(
                attachmentsFiles.map(async file => {
                  const fileBlob = await getFile(file.id) // Get the actual file (blob)
                  const fileInfo = await getFileInfo(file.id) // Get the file info (fileName, mimeType, url)
                  return new File([fileBlob], fileInfo.fileName, { type: fileInfo.mimeType })
                })
              )
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        showLoader.value = false
      }
    })

    return {
      logo,
      title,
      category,
      eventDateText,
      eventDatePickerModel,
      shortDescription,
      shortTextContent,
      textContent,
      titleImage,
      additionalImages,
      files,
      formValid,
      categories,
      menu,
      showLoader,
      rules,
      updateEventDate,
      submitForm,
      resizeImage,
      route,
      form,
      isEdit,
      isNew,
      isTitleChanged,
      isEventDateChanged,
      isShortDescriptionChanged,
      isShortTextContentChanged,
      isAdditionalImagesChanged,
      isFilesChanged,
      isTextContentChange,
      editForm,
      onTitleImageChange,
      onAdditionalImagesChange,
      onFilesChange,
      apiInfoCategories,
      isAnythingChanged,
      router,
      goBack,
      updateCategory,
      firebaseConfig,
      submitFirestore
    }
  },
})
</script>

<style scoped>
.create-edit-infoitem {
  padding: 1rem;
}

::v-deep .v-date-picker .mdi-chevron-right {
  margin-left: -2rem;
  /* Add any other style customizations here */
}

/* Example of more targeted customization, if necessary */
::v-deep .v-date-picker .v-icon.mdi-chevron-right {
  margin-left: -2rem;
}

.toolbar-icon-white {
  height: 30px;
  filter: brightness(0) invert(1);
}

.create-edit-info-item {
  max-width: 900px;
  margin: 2rem auto;
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.upload-section {
  margin: 2rem 0;
}

.file-info-text {
  font-size: 0.85rem;
  color: #666;
  margin-top: -12px;
  margin-bottom: 24px;
}

h4 {
  font-weight: bold;
  color: #007bff;
  margin-bottom: 1rem;
}

.v-text-field,
.v-textarea,
.v-file-input,
.v-select,
.v-html-editor {
  margin-bottom: 1.5rem;
}

/* Added styles to make the chips more visible */
.v-chip {
  background-color: #e0f7fa !important;
  color: #007bff !important;
  margin: 4px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .create-edit-info-item {
    padding: 1.5rem;
    max-width: 100%;
  }
}
</style>
